import type { ChatEntry } from "../chat/chatStore";
import type { AlertReview } from "./types";

export type WatchSetupSource = "direct" | "conversation";
export type WatchSetupAnswer = { answer: string; custom: boolean };

export type WatchSetupState = {
  source: WatchSetupSource | null;
  review: AlertReview | null;
  round: number;
  questionIndex: number | null;
  answers: Record<string, WatchSetupAnswer>;
  baseTranscript: ChatEntry[];
  transcript: ChatEntry[];
  reviewing: boolean;
};

export const initialWatchSetupState = (): WatchSetupState => ({
  source: null,
  review: null,
  round: 1,
  questionIndex: null,
  answers: {},
  baseTranscript: [],
  transcript: [],
  reviewing: false,
});

export const answersForReview = (
  answers: Record<string, WatchSetupAnswer>,
  review: AlertReview,
): Record<string, WatchSetupAnswer> =>
  Object.fromEntries(
    review.questions.flatMap(question =>
      answers[question.id] ? [[question.id, answers[question.id]]] : [],
    ),
  );

export type WatchSetupAction =
  | { type: "reset" }
  | { type: "begin"; source: WatchSetupSource; baseTranscript?: ChatEntry[] }
  | { type: "set_reviewing"; reviewing: boolean }
  | {
      type: "reviewed";
      source: WatchSetupSource;
      review: AlertReview;
      round: number;
      baseTranscript: ChatEntry[];
      transcript: ChatEntry[];
    }
  | { type: "set_question"; index: number | null }
  | { type: "set_answer"; questionId: string; answer: WatchSetupAnswer }
  | { type: "back_to_prompt" };

/**
 * Setup is a small state machine, not a collection of independent values.
 * Keeping its transitions here prevents partial resets and impossible mixes
 * such as answers from an old review attached to a new one.
 */
export const watchSetupReducer = (
  state: WatchSetupState,
  action: WatchSetupAction,
): WatchSetupState => {
  switch (action.type) {
    case "reset":
      return initialWatchSetupState();
    case "begin":
      return {
        ...initialWatchSetupState(),
        source: action.source,
        baseTranscript: action.baseTranscript ?? [],
      };
    case "set_reviewing":
      return { ...state, reviewing: action.reviewing };
    case "reviewed":
      return {
        ...state,
        source: action.source,
        review: action.review,
        round: action.round,
        questionIndex: action.review.questions.length > 0 ? 0 : null,
        answers: answersForReview(state.answers, action.review),
        baseTranscript: action.baseTranscript,
        transcript: action.transcript,
      };
    case "set_question":
      return { ...state, questionIndex: action.index };
    case "set_answer":
      return { ...state, answers: { ...state.answers, [action.questionId]: action.answer } };
    case "back_to_prompt":
      return {
        ...state,
        review: null,
        round: 1,
        questionIndex: null,
        answers: {},
        transcript: [],
      };
  }
};
